<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { backend_url } from '@/config/config';
import dartsHubLogo from '@/assets/dartshub-logo-blanc.png';

const route = useRoute();
const router = useRouter();

const consentToken = ref('');
const clientName = ref('');
const loading = ref(false);
const error = ref('');

onMounted(() => {
  // Deux origines possibles : navigation SPA (LoginPage/RegisterPage/GoogleCompletePage,
  // query string) ou redirection HTTP brute du backend (Google, fragment d'URL).
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const hashParams = new URLSearchParams(hash);
  consentToken.value = hashParams.get('consent_token') || (route.query.consent_token as string) || '';
  clientName.value = hashParams.get('client_name') || (route.query.client_name as string) || '';
  if (hash) history.replaceState(null, '', window.location.pathname + window.location.search);

  if (!consentToken.value) router.replace('/login');
});

const authorize = async () => {
  loading.value = true;
  error.value = '';
  try {
    const { data } = await axios.post(`${backend_url}/partners/consent/confirm`, {
      consent_token: consentToken.value,
    });
    const fragment = new URLSearchParams({ token: data.identity_token }).toString();
    window.location.href = `${data.redirect_uri}#${fragment}`;
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Erreur lors de la validation. Le lien a peut-être expiré.';
    loading.value = false;
  }
};

const cancel = () => router.push('/');
</script>

<template>
  <div class="dh-page">
    <div class="dh-wrapper">

      <div class="dh-header">
        <div class="dh-header-content">
          <div class="dh-brand">
            <img :src="dartsHubLogo" alt="DartsHub" class="dh-brand-logo" />
          </div>
          <div class="dh-tricolor-bar">
            <span></span><span></span><span></span>
          </div>
          <p class="dh-subtitle">Autorisation requise</p>
        </div>
      </div>

      <div class="dh-body">
        <div class="dh-consent-icon"><i class="fa-solid fa-shield-halved" /></div>
        <p class="dh-info">
          <strong>{{ clientName || 'Ce site' }}</strong> souhaite utiliser votre compte DartsHub pour vous
          identifier. En autorisant, vous partagez avec ce site :
        </p>
        <ul class="dh-share-list">
          <li><i class="fa-solid fa-check" /> Nom et prénom</li>
          <li><i class="fa-solid fa-check" /> Pseudo</li>
          <li><i class="fa-solid fa-check" /> Adresse email</li>
        </ul>
        <p class="dh-hint">
          Aucune autre information (mot de passe, licence, club, rôle) n'est jamais partagée.
          Vous pourrez révoquer cette autorisation à tout moment depuis votre profil DartsHub.
        </p>

        <div v-if="error" class="dh-msg fd-msg--error">{{ error }}</div>

        <button class="dh-btn fd-btn-primary" :disabled="loading" @click="authorize">
          <i v-if="loading" class="fa-solid fa-spinner fa-spin" />
          <span>{{ loading ? 'Validation…' : `Autoriser${clientName ? ' ' + clientName : ''}` }}</span>
        </button>
        <button class="dh-btn dh-btn-cancel" :disabled="loading" @click="cancel">Annuler</button>
      </div>

      <div class="dh-footer">
        <span>Authentification sécurisée par</span>
        <strong>&nbsp;DartsHub</strong>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

.dh-page {
  min-height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: var(--bg);
}

.dh-wrapper {
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'Segoe UI', sans-serif;
  background: var(--bg-card);
  color: var(--text);
  border-radius: 20px;
  overflow: hidden;
  width: 420px;
  max-width: 100%;
  box-shadow: 0 8px 40px color-mix(in srgb, var(--primary) 10%, transparent);
}

.dh-header {
  background: linear-gradient(145deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%);
  text-align: center;
}

.dh-header-content {
  padding: 22px 28px 16px;
}

.dh-brand {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.dh-brand-logo {
  height: 2.25rem;
  width: auto;
}

.dh-tricolor-bar {
  display: flex;
  height: 4px;
  width: 110px;
  margin: 0 auto 12px;
  border-radius: 10px;
  overflow: hidden;
  gap: 3px;
}

.dh-tricolor-bar span { flex: 1; border-radius: 10px; }
.dh-tricolor-bar span:nth-child(1) { background: var(--dh-bar-1); }
.dh-tricolor-bar span:nth-child(2) { background: var(--dh-bar-2); }
.dh-tricolor-bar span:nth-child(3) { background: var(--primary); }

.dh-subtitle {
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 14px;
  font-weight: 300;
}

.dh-body {
  padding: 24px 28px;
  flex: 1;
}

.dh-consent-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
  font-size: 1.8rem;
  color: var(--primary);
}

.dh-info {
  font-size: 0.88rem;
  color: var(--text);
  margin: 0 0 14px;
  line-height: 1.5;
  text-align: center;
}

.dh-share-list {
  list-style: none;
  margin: 0 0 16px;
  padding: 12px 16px;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dh-share-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.86rem;
  color: var(--text);
}

.dh-share-list i {
  color: var(--success);
  font-size: 0.8rem;
}

.dh-hint {
  font-size: 0.76rem;
  color: var(--text-muted);
  line-height: 1.4;
  margin: 0 0 18px;
}

.dh-btn {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
}

.dh-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.dh-btn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--primary) 28%, transparent);
}

.dh-btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--primary-hover) 0%, color-mix(in srgb, var(--primary) 60%, #000) 100%);
  transform: translateY(-1px);
}

.dh-btn-cancel {
  background: transparent;
  color: var(--text-muted);
  border: none;
  font-size: 0.82rem;
  font-weight: 500;
  margin-bottom: 0;
  opacity: 0.7;
}

.dh-btn-cancel:hover:not(:disabled) {
  opacity: 1;
  text-decoration: underline;
}

.dh-msg {
  font-size: 0.83rem;
  font-weight: 500;
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  margin-bottom: 12px;
}

.dh-msg--error {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

.dh-footer {
  background: var(--bg-input);
  border-top: 1px solid var(--border);
  padding: 10px 16px;
  text-align: center;
  font-size: 0.72rem;
  color: var(--text-muted);
}

.dh-footer strong {
  color: var(--primary);
  font-weight: 600;
}
</style>
